// OrdersPage.tsx
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Avatar,
  Chip,
  Skeleton,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  Grid,
  Slider,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getOrders,
  getHandymanProposals,
  getHandymanAvailableOrders,
  updateHandymanProposal,
  sendHandymanProposal,
} from "./actions";
import {
  OrderViewModel,
  OrderProposalViewModel,
  OrderStatus,
} from "../../configuration/api/apiSchema";
import { HandymanAppState } from "../../configuration/redux/reducers";
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import CancelIcon from "@mui/icons-material/Cancel";
import ChatIcon from "@mui/icons-material/Chat";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CategoryIcon from "@mui/icons-material/Category";
import AddIcon from "@mui/icons-material/Add";
import debounce from 'lodash/debounce';

import { createOrGetChat } from "./actions";
import { mapOrderStatusToString } from "./OrderDetailsPage";
import { Autocomplete } from "@react-google-maps/api";

// Tab panel helper
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props;
  return value === index ? <Box sx={{ p: 2 }}>{children}</Box> : null;
}

// Add this component for the skeleton loader
const OrdersSkeleton = () => (
  <Box>
    {[1, 2, 3].map((index) => (
      <Card key={index} sx={{ mb: 2, borderRadius: 2 }}>
        <CardHeader
          avatar={<Skeleton variant="circular" width={40} height={40} />}
          title={<Skeleton variant="text" width={200} height={24} />}
          subheader={<Skeleton variant="text" width={150} height={20} />}
        />
        <CardContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <Skeleton variant="text" width="100%" height={20} />
            <Box display="flex" gap={2}>
              <Skeleton variant="text" width={100} height={20} />
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
            </Box>
          </Box>
        </CardContent>
      </Card>
    ))}
  </Box>
);

const OrderCard = ({ order, onChat, onCancel, role }: {
  order: OrderViewModel;
  onChat: (e: React.MouseEvent, handymanId: string) => void;
  onCancel: (id: string) => void;
  role: string;
}) => {
  const navigate = useNavigate();

  // Helper function to handle cancel
  const handleCancel = (id: string | undefined) => {
    if (id) {
      onCancel(id);
    }
  };

  // Determine which user info to display based on role
  const displayUser = role === "customer" ? order.handyman : order.customer;
  const userTitle = role === "customer" ? "Handyman" : "Customer";

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: 2,
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: 3,
        },
        cursor: 'pointer',
        maxWidth: '100%',
      }}
      onClick={() => order.id && navigate(`/orders/${order.id}`)}
    >
      <CardHeader
        avatar={
          <Avatar
            src={displayUser?.avatarUrl || "https://via.placeholder.com/40"}
            alt={displayUser ? `${displayUser.firstName} ${displayUser.lastName}` : userTitle}
            sx={{
              width: 40,
              height: 40,
              border: '2px solid',
              borderColor: 'primary.main',
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (role === "customer" && order.handyman?.id) {
                navigate(`/browse-handyman/${order.handyman.id}`);
              }
            }}
          />
        }
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
              {order.name}
            </Typography>
            <Chip
              label={mapOrderStatusToString(order.status)}
              color={
                order.status === OrderStatus.InProgress
                  ? "success"
                  : order.status === OrderStatus.Completed
                    ? "primary"
                    : "default"
              }
              size="small"
              sx={{ height: 24 }}
            />
          </Box>
        }
        subheader={
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              {displayUser ? `${displayUser.firstName} ${displayUser.lastName}` : `No ${userTitle.toLowerCase()} assigned`}
            </Typography>
            {order.createdAtUtc && (
              <Typography variant="caption" color="text.secondary">
                • Created {new Date(order.createdAtUtc).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        }
      />
      <Divider />
      <CardContent>
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography
            variant="body1"
            color="text.primary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {order.description || "No description provided"}
          </Typography>

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            {order.price && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <AttachMoneyIcon color="primary" fontSize="small" />
                <Typography variant="body2" fontWeight="medium">${order.price}</Typography>
              </Box>
            )}
            {order.dueDateUtc && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <AccessTimeIcon color="primary" fontSize="small" />
                <Typography variant="body2">
                  Due: {new Date(order.dueDateUtc).toLocaleString()}
                </Typography>
              </Box>
            )}
            {order.category?.name && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <CategoryIcon color="primary" fontSize="small" />
                <Typography variant="body2">{order.category.name}</Typography>
              </Box>
            )}
            {order.location && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <LocationOnIcon color="primary" fontSize="small" />
                <Typography variant="body2">{order.location}</Typography>
              </Box>
            )}
          </Stack>

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              {role === "customer" && order.status === OrderStatus.InProgress && order.id && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancel(order.id);
                  }}
                >
                  Cancel Order
                </Button>
              )}
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                order.id && navigate(`/orders/${order.id}`);
              }}
              sx={{ borderRadius: 2 }}
            >
              View Details
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const OrdersPage = () => {
  // Replace mapStateToProps with useSelector
  const {
    isLoading,
    orders,
    handymanProposals,
    handymanAvailableOrders,
    handymanProposalsLoading,
    handymanAvailableOrdersLoading,
  } = useSelector((state: HandymanAppState) => state.order);


  const [filterAddress, setFilterAddress] = useState<string | null>(null);
  const [radiusKm, setRadiusKm] = useState<number>(10);
  const [autocompleteRef, setAutocompleteRef] = useState<google.maps.places.Autocomplete | null>(null);

  const role = useSelector((state: HandymanAppState) => state.user?.authData?.role ?? "customer");
  const currentUserId = useSelector((state: HandymanAppState) => state.user?.currentUserDetails?.id);

  const dispatch = useDispatch<ThunkDispatch<HandymanAppState, unknown, AnyAction>>();

  // Memoize action creators
  const memoizedGetOrders = useCallback(() => {
    dispatch(getOrders());
  }, [dispatch]);

  const memoizedGetHandymanProposals = useCallback(() => {
    dispatch(getHandymanProposals());
  }, [dispatch]);

  const memoizedGetHandymanAvailableOrders = useCallback(
    (filters: { address: string | null; radiusKm: number | null }) => {
      dispatch(getHandymanAvailableOrders(filters));
    },
    [dispatch]
  );

  const memoizedUpdateHandymanProposal = useCallback(
    (orderProposalId: string, status: "Confirmed" | "Rejected") => {
      return dispatch(updateHandymanProposal(orderProposalId, status));
    },
    [dispatch]
  );

  const memoizedSendHandymanProposal = useCallback(
    (orderId: string) => {
      return dispatch(sendHandymanProposal(orderId));
    },
    [dispatch]
  );

  const [tabValue, setTabValue] = useState(0);
  const navigate = useNavigate();

  // Local state to track per-item loading for proposal updates and order applications
  const [updatingProposals, setUpdatingProposals] = useState<string[]>([]);
  const [applyingOrders, setApplyingOrders] = useState<string[]>([]);

  const debouncedFetchOrders = useCallback(
    debounce((address: string | null, radius: number | null) => {
      dispatch(getHandymanAvailableOrders({
        address,
        radiusKm: radius,
      }));
    }, 1000), // 500ms delay
    [dispatch]
  );

  // On mount
  useEffect(() => {
    // Always load "Active" orders
    memoizedGetOrders();
  }, [filterAddress, radiusKm]);

   // On mount
   useEffect(() => {
    if (role === "handyman") {
      debouncedFetchOrders(filterAddress, radiusKm);
      memoizedGetHandymanProposals();
    }
  }, [filterAddress, radiusKm, role]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleChatWithHandyman = async (
    e: React.MouseEvent,
    handymanId: string
  ) => {
    e.stopPropagation(); // prevent parent container from also firing
    if (!currentUserId) return;

    try {
      // Wait for the createOrGetChat call to complete
      const chatId: any = await dispatch(createOrGetChat([currentUserId, handymanId]) as any);

      // If it returned a valid chat object with an "id", then navigate to that chat
      if (chatId) {
        navigate(`/chats/${chatId}`);
      }
    } catch (error) {
      // Handle any errors if needed (e.g. show a toast)
      console.error("Failed to create or get chat:", error);
    }
  };

  // Active Orders: cancel order (demo)
  const handleCancelOrder = (id: string) => {
    console.log("Cancel order with id:", id);
  };

  // Navigate to order details
  const handleOrderClick = (orderId: string) => {
    navigate(`/orders/${orderId}`);
  };

  // Helper to decide badge color for proposal status
  const getStatusChipColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "confirmed":
        return "success";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  // Handyman: Accept or Reject a proposal with loader per proposal
  const handleAcceptProposal = (proposalId: string) => {
    setUpdatingProposals((prev) => [...prev, proposalId]);
    memoizedUpdateHandymanProposal(proposalId, "Confirmed").finally(() => {
      setUpdatingProposals((prev) => prev.filter((id) => id !== proposalId));
      memoizedGetOrders()
    });
  };

  const handleRejectProposal = (proposalId: string) => {
    setUpdatingProposals((prev) => [...prev, proposalId]);
    memoizedUpdateHandymanProposal(proposalId, "Rejected").finally(() => {
      setUpdatingProposals((prev) => prev.filter((id) => id !== proposalId));
      memoizedGetOrders()
    });
  };

  // Handyman: Apply for an available order with loader
  const handleApplyToOrder = (orderId: string) => {
    setApplyingOrders((prev) => [...prev, orderId]);

    memoizedSendHandymanProposal(orderId).finally(() => {
      setApplyingOrders((prev) => prev.filter((id) => id !== orderId));

      memoizedGetHandymanAvailableOrders({
        address: filterAddress,
        radiusKm: radiusKm,
      });
    });
  };

  // Fix the status comparison
  const isActiveOrder = (status: OrderStatus | undefined): boolean => {
    return status === OrderStatus.InProgress;
  };

  // Fix the proposal status check
  const isRequestedProposal = (status: string | undefined): boolean => {
    return status?.toLowerCase() === "requested";
  };

  // Fix the proposal actions
  const handleProposalAction = (proposalId: string | undefined, action: (id: string) => void) => {
    if (proposalId) {
      action(proposalId);
    }
  };

  // Helper function to handle apply to order
  const handleApplyToOrderWithCheck = (orderId: string | undefined) => {
    if (orderId) {
      handleApplyToOrder(orderId);
    }
  };

  return (
    <Box
      sx={{
        background: "linear-gradient(to bottom, #f5f5f5, #ffffff)",
        minHeight: "100vh",
        py: 10,
      }}
    >
      <Box
        width={{ xs: "90%", sm: "80%", md: "75%" }}
        mx="auto"
        mt={4}
        p={{ xs: 2, sm: 4 }}
        component={Paper}
        elevation={4}
        sx={{ borderRadius: 4 }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" fontWeight="bold">
            Orders
          </Typography>
          {role === "customer" && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/create-order')}
              sx={{ borderRadius: 2 }}
            >
              Add Order
            </Button>
          )}
        </Box>

        {/* TABS */}
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Active Orders" />
          {role === "handyman" && <Tab label="Proposals" />}
          {role === "handyman" && <Tab label="Browse Orders" />}
        </Tabs>

        {/* TAB PANEL: ACTIVE ORDERS */}
        <TabPanel value={tabValue} index={0}>
          {isLoading ? (
            <OrdersSkeleton />
          ) : orders.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary">
                No active orders found
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/create-order')}
                sx={{ mt: 2, borderRadius: 2 }}
              >
                Create New Order
              </Button>
            </Box>
          ) : (
            <Box>
              {orders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onChat={handleChatWithHandyman}
                  onCancel={handleCancelOrder}
                  role={role}
                />
              ))}
            </Box>
          )}
        </TabPanel>

        {/* TAB PANEL: PROPOSALS */}
        {role === "handyman" && (
          <TabPanel value={tabValue} index={1}>
            {handymanProposalsLoading ? (
              <OrdersSkeleton />
            ) : handymanProposals.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="h6" color="text.secondary">
                  No proposals found
                </Typography>
              </Box>
            ) : (
              <Box>
                {handymanProposals.map((proposal) => {
                  const disableActions =
                    proposal.status?.toLowerCase() !== "requested" ||
                    updatingProposals.includes(proposal.id!);

                  return (
                    <Card
                      key={proposal.id}
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        transition: 'box-shadow 0.2s',
                        '&:hover': {
                          boxShadow: 3,
                        },
                      }}
                    >
                      <CardHeader
                        avatar={
                          <Avatar
                            src={proposal.order?.customer?.avatarUrl || "https://via.placeholder.com/40"}
                            alt={proposal.order?.customer ? `${proposal.order.customer.firstName} ${proposal.order.customer.lastName}` : "Customer"}
                            sx={{ width: 40, height: 40 }}
                          />
                        }
                        title={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              {proposal.order?.name}
                            </Typography>
                            <Chip
                              label={proposal.status}
                              color={getStatusChipColor(proposal.status ?? "")}
                              size="small"
                              sx={{ height: 24 }}
                            />
                          </Box>
                        }
                        subheader={
                          <Typography variant="body2" color="text.secondary">
                            {proposal.order?.customer ? `${proposal.order.customer.firstName} ${proposal.order.customer.lastName}` : "Unknown Customer"}
                          </Typography>
                        }
                      />
                      <Divider />
                      <CardContent>
                        <Box display="flex" flexDirection="column" gap={2}>
                          <Typography variant="body1" color="text.primary">
                            {proposal.order?.description || "No description provided"}
                          </Typography>

                          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                            {proposal.order?.price && (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <AttachMoneyIcon color="primary" fontSize="small" />
                                <Typography variant="body2">${proposal.order.price}</Typography>
                              </Box>
                            )}
                            {proposal.order?.dueDateUtc && (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <AccessTimeIcon color="primary" fontSize="small" />
                                <Typography variant="body2">
                                  Due: {new Date(proposal.order.dueDateUtc).toLocaleString()}
                                </Typography>
                              </Box>
                            )}
                            {proposal.order?.category?.name && (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <CategoryIcon color="primary" fontSize="small" />
                                <Typography variant="body2">{proposal.order.category.name}</Typography>
                              </Box>
                            )}
                            {proposal.order?.location && (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <LocationOnIcon color="primary" fontSize="small" />
                                <Typography variant="body2">{proposal.order.location}</Typography>
                              </Box>
                            )}
                          </Stack>

                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box display="flex" alignItems="center" gap={1}>
                              <IconButton
                                color="primary"
                                onClick={() => window.open("/chats", "_blank")}
                                title="Chat with Customer"
                              >
                                <ChatIcon />
                              </IconButton>
                              {!disableActions && isRequestedProposal(proposal.status) && (
                                <>
                                  <Button
                                    variant="outlined"
                                    color="success"
                                    onClick={() => handleProposalAction(proposal.id, handleAcceptProposal)}
                                    disabled={updatingProposals.includes(proposal.id!)}
                                  >
                                    {updatingProposals.includes(proposal.id!) ? (
                                      <CircularProgress size={20} color="inherit" />
                                    ) : (
                                      "Accept"
                                    )}
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => handleProposalAction(proposal.id, handleRejectProposal)}
                                    disabled={updatingProposals.includes(proposal.id!)}
                                  >
                                    {updatingProposals.includes(proposal.id!) ? (
                                      <CircularProgress size={20} color="inherit" />
                                    ) : (
                                      "Reject"
                                    )}
                                  </Button>
                                </>
                              )}
                            </Box>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => navigate(`/orders/${proposal.order?.id}`)}
                              sx={{ borderRadius: 2 }}
                            >
                              View Details
                            </Button>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
          </TabPanel>
        )}

        {/* TAB PANEL: BROWSE ORDERS */}
        {role === "handyman" && (
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={3}>
              {/* Left Column - Filters */}
              <Grid item xs={12} md={3}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                    Filters
                  </Typography>

                  <Autocomplete
                    onLoad={(autocomplete) => setAutocompleteRef(autocomplete)}
                    onPlaceChanged={() => {
                      const place = autocompleteRef?.getPlace();
                      if (place?.formatted_address) {
                        setFilterAddress(place.formatted_address);
                      }
                    }}
                  >
                    <TextField
                      label="Search Address"
                      variant="outlined"
                      fullWidth
                      value={filterAddress ?? ""}
                      onChange={(e) => setFilterAddress(e.target.value)}
                      sx={{ mb: 2 }}
                    />
                  </Autocomplete>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Search Radius (km)
                  </Typography>
                  <Slider
                    value={radiusKm}
                    onChange={(e, value) => setRadiusKm(value as number)}
                    min={0}
                    max={50}
                    step={0.5}
                    valueLabelDisplay="auto"
                  />
                </Paper>
              </Grid>

              {/* Right Column - Orders List */}
              <Grid item xs={12} md={9}>
                {handymanAvailableOrdersLoading ? (
                  <OrdersSkeleton />
                ) : handymanAvailableOrders.length === 0 ? (
                  <Box textAlign="center" py={4}>
                    <Typography variant="h6" color="text.secondary">
                      No available orders found
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    {handymanAvailableOrders.map((order) => (
                      <OrderCard
                        key={order.id}
                        order={order}
                        onChat={handleChatWithHandyman}
                        onCancel={handleCancelOrder}
                        role={role}
                      />
                    ))}
                  </Box>
                )}
              </Grid>
            </Grid>
          </TabPanel>
        )}
      </Box>
    </Box>
  );
};

export default OrdersPage;
